import { fn, INamed } from '../utils';
import FindFirst = fn.FindFirst;
import ToDictionary = fn.ToDictionary;
import Notifications = fn.Notifications;
import { Bind as kBind } from '../navkendo/kutils';
import { IPageProperty } from '../nav/navtypes';
import { NavClientService } from './navclientservice';
import { NavItemAnchor } from './navitemanchor';
import { NavPropertyItem } from './navpropertyitem';
import { IPropertyInfoCallback } from '../nav/navtypes';
import { Observable } from './navmodel';
import { NavPageItem } from './navpageitem';

/** Callback Args for PropertyContainer State Change Callback */
export type NavPropertyContainerCallback = { Property: IPageProperty };
/** Change Aware Container of MarkerValue per Property right not thre are 4 levels of manipulation: (see : GetValue)
* 1.) forceAll : set all as Marked
* 2.) ReplaceValues : set named per Property explicit State (for all line items if any)
* 3.) RegisterPerItemResolution : set static values (per line item if any)
* 4.) RegisterDynamic: full dynamic resolution --> get's called "very often!" (might even be per Value Demand)
*/
export class NavPropertyContainer extends Observable {
    constructor(private readonly Anchor: NavItemAnchor<any>, private id: string) {
        super();
        this.allProps = Anchor.Source.ClientInfo.AllProperties;
        this.Service = Anchor.Service;
    }
    private readonly Service: NavClientService;
    private allProps: INamed<IPageProperty>;

    /** Set Global "Mark All As Checked" state */
    public SetForceAll(value: boolean) {
        value = !!value;
        if (value !== !!this.forceAll) {
            this.forceAll = value;
            fn.DictToArray(this.allProps).forEach(p => this.fireChange(p));
        }
    }
    private forceAll: boolean;

    /** Reset ALL State for given name */
    public ReplaceValues(name: string, args: Array<string | IPageProperty>) {
        if (!(typeof name == "string"))
            throw new Error("Invalid Parameter type for name");
        args = <Array<string | IPageProperty>>$.map(args, fn.identity); // flatten array if necessary (_args might be [][])
        let previousState = ToDictionary(this.GetItems(name), fn.identity),
            values = this.values;

        args.forEach(item => {
            const p = this.resolveProperty(item);
            const path = p.Path;

            if (!values[path]) {
                values[path] = {};
                values[path][name] = true;
                this.fireChange(p);
            } else {
                // already Marked (evtl. by other token)
                values[path][name] = true;
            }

            delete previousState[path];
        }, this);

        // Reset Remaining Previous Values
        Object.getOwnPropertyNames(previousState).forEach(item => {
            var p = this.resolveProperty(item),
                path = p.Path;

            delete values[path][name];
            if (Object.getOwnPropertyNames(values[path]).length == 0) {
                delete values[path];
                this.fireChange(p);
            }
        }, this);
    }

    /** Register Dynamic Dependency for given properties state, TODO : Unify with "resolvePerItemInfo" */
    public RegisterDynamic(prop: string | IPageProperty, calculate: string | ((item: NavPropertyItem, prop: IPageProperty) => boolean)): () => void {
        if (typeof calculate === "string") {
            var formula = this.Service.GetEvalFormula(calculate);
            return this.RegisterDynamic(prop, formula);
        } else {
            var property = this.resolveProperty(prop),
                propertyPath = property.Path;

            var dynamicValueState = this.dynamicValueState;
            if (!dynamicValueState) {
                dynamicValueState = this.dynamicValueState = {};
                // TODO : this way every value changed will force Recalculation every time
                this.Anchor.RegisterPropertyChanged(null, e => {
                    fn.DictToArray(dynamicValueState).forEach(p => {
                        delete p.Value;
                        this.fireChange(p.Property);
                    });
                });
            }

            var dynamic = dynamicValueState[propertyPath] || (dynamicValueState[propertyPath] = {
                Property: property, Calc: [], Value: undefined
            });

            var callbacks = dynamic.Calc;
            dynamic.Calc.push(calculate);

            // trigger first Validation
            this.fireChange(property);

            return fn.once(() => {
                var idx = dynamic.Calc.indexOf(calculate);
                if (idx >= 0) {
                    callbacks.splice(idx, 1);
                    if (callbacks.length == 0)
                        delete dynamicValueState[propertyPath];
                }
            });
        }
    }
    /** Per Property Path Registered dynamic value state registration */
    private dynamicValueState: {
        [propPath: string]: {
            Property: IPageProperty,
            /** Per Clientkey Current State */
            Value: INamed<boolean>,
            Calc: Array<(item: NavPropertyItem, prop: IPageProperty) => boolean>
        }
    };

    /** Get Current State for property on given Item */
    public GetValue(prop: string | IPageProperty, item: NavPropertyItem) {
        var p = this.resolveProperty(prop),
            path = p && p.Path;

        if (!path)
            return;

        //1.) check global marker
        if (this.forceAll)
            return true;

        // 2.) check per path marker
        var result = !!this.values[path];

        // 3.) check per item static values
        if (this.id) {
            // Resolve Custom Per Item Information (once per Item ...)
            var attachId = "__Custom" + this.id;
            var customProps = <INamed<boolean>>item[attachId] || (item[attachId] = (this.resolvePerItemInfo(item) || <INamed<boolean>>{}));

            if (path in customProps) {
                result = customProps[path];
            }
        }

        // 4.) Force Calculation via fully dynamic dependencies
        if (!result) {
            let dynamic = this.dynamicValueState && this.dynamicValueState[path];
            if (dynamic) {
                var checkIt = dynamic.Value || (dynamic.Value = {});
                if (item.ClientKey in checkIt)
                    result = checkIt[item.ClientKey];
                else
                    result = checkIt[item.ClientKey] = !!FindFirst(dynamic.Calc, c => c(item, p) == true);
            }
        }
        return result;
    }

    /** Get Registered Names */
    public GetItems(name?: string) {
        var values = this.values,
            result = Object.getOwnPropertyNames(values);
        if (name)
            result = result.filter(check => values[check][name], this);
        else if (this.dynamicValueState) {
            // add dynamic Values if available (Caution : must be revalidated!)
            for (var dynName in this.dynamicValueState) {
                if (result.indexOf(dynName) < 0)
                    result.push(dynName);
            }
        }
        return result;
    }

    /** RegisterChange [prop = null : get notified about all changes] */
    public RegisterChanged(prop: any, cb: (arg: NavPropertyContainerCallback) => void) {
        if (!prop || prop == "*") // means : all Props
            prop = "*";
        else
            prop = this.resolveProperty(prop).Path;
        var notifications = this.notifications;
        var r = notifications[prop] || (notifications[prop] = new Notifications<{ Property: IPageProperty; Value: boolean }>());
        return r.bind(cb);
    }

    /** Register Custom Resolution of Names per item (Root or Line Item),
     * - Callback gets called once per Item (Root or line Item)
     * - so far no dynamic (on Value Changed ...)
     * - Return function deregisters Callback again  */
    public RegisterPerItemResolution(callback: IPropertyInfoCallback) {

        // TODO!! Refactory (Problem Registration too late in KWFEdit.ts)
        const id = "__Custom" + this.id;
        var source = this.Anchor && this.Anchor.Source;
        if (source) {
            delete source[id];
            var s = source as NavPageItem;
            if (typeof s.GetLineData == "function") {
                s.MetaData.Properties.filter(item => !!item.ArrayProperties).forEach(prop => {
                    var p = s.HasLineData(prop);
                    if (p && p.Count > 0) {
                        p.Items.forEach(pItem => {
                            delete pItem[id];
                        });
                    }
                });
            }
        }
        return kBind(this, callback, "ResolvePerItemInfo");
    }

    private resolvePerItemInfo(item: NavPropertyItem) {
        var result = <INamed<boolean>>{};
        var callParams = { ExplicitState: result, Item: item, Container: this };
        this.trigger("ResolvePerItemInfo", callParams);
        return result;
    };

    fireChange(property: IPageProperty) {
        [this.notifications[property.Path], this.notifications["*"]].forEach(r => r && r.trigger({ Property : property }));
    }

    /** 'hard' resolve to Property Item: fails if not available */
    private resolveProperty(prop: string | IPageProperty): IPageProperty {
        if (typeof prop === "string") {
            var result = this.allProps[prop];
        }
        else if (IsPageProperty(prop)) {
            var result = this.allProps[prop.Path];
        }

        if (!result)
            throw new Error("Property '" + prop + "' not found!");

        return result;

        function IsPageProperty(prop: any): prop is IPageProperty { return prop && prop.Path; }
    }
    /** Marker Container : Registered Names Marking Per Property (static!, got extended via "dynamicValueState" ... */
    private values: INamed<INamed<boolean>> = {};
    private notifications: INamed<Notifications<NavPropertyContainerCallback>> = <any>{};
}
